import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, ModalBody } from "reactstrap";
import ConfirmModal from "../../Components/Common/ConfirmModal";
import { AppDispatch } from "../../store";
import { showAlertWithTimeout } from "../../slices/Alert/reducer";
import { resolveApiMessage } from "../../helpers/apiMessage";
import { getCategoryMenuCount } from "../../helpers/helpers";
import HeaderSidebarToggle from "../../Components/Common/HeaderSidebarToggle";
import HomeLogoLink from "../../Components/Common/HomeLogoLink";
import {
    createMenuCategory,
    deleteMenuCategory,
    getMenuCategories,
    updateMenuCategory,
    reorderMenuCategories,
} from "../../slices/Menu/thunk";

const getCategoryId = (category: any): string | number | undefined => {
    if (!category || typeof category !== "object") {
        return undefined;
    }

    const candidateKeys = ["id", "uuid", "_id", "categoryId", "slug"];

    for (const key of candidateKeys) {
        const value = (category as Record<string, any>)[key];
        if (value !== undefined && value !== null && value !== "") {
            return value as string | number;
        }
    }

    return undefined;
};

const getCategorySortOrder = (category: any): number => {
    const value = Number(category?.sort_order ?? category?.sortOrder ?? 0);
    return Number.isFinite(value) ? value : 0;
};

const sortCategoriesByOrder = (items: any[]) =>
    [...items].sort((left, right) => {
        const orderDiff = getCategorySortOrder(left) - getCategorySortOrder(right);
        if (orderDiff !== 0) {
            return orderDiff;
        }

        return Number(getCategoryId(left) ?? 0) - Number(getCategoryId(right) ?? 0);
    });

const areCategoryOrdersEqual = (left: any[], right: any[]) =>
    left.length === right.length &&
    left.every((category, index) => getCategoryId(category) === getCategoryId(right[index]));

const getCategoryName = (category: any): string => {
    if (!category || typeof category !== "object") {
        return "Danh mục chưa đặt tên";
    }

    const candidateKeys = ["name", "title", "label"];

    for (const key of candidateKeys) {
        const value = (category as Record<string, any>)[key];
        if (typeof value === "string" && value.trim().length > 0) {
            return value.trim();
        }
    }

    return "Danh mục chưa đặt tên";
};

const MenuCategory = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { categories, loading, error } = useSelector((state: any) => state.MenuList);
    const categoriesList = useMemo(() => (Array.isArray(categories) ? categories : []), [categories]);

    const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState("");
    const [categoryNameError, setCategoryNameError] = useState("");
    const CATEGORY_NAME_MAX_LENGTH = 50;

    const openAddCategoryModal = () => {
        setNewCategoryName("");
        setCategoryNameError("");
        setShowAddCategoryModal(true);
    };

    const closeAddCategoryModal = () => {
        setShowAddCategoryModal(false);
        setNewCategoryName("");
        setCategoryNameError("");
    };

    const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
    const [deleteCategoryId, setDeleteCategoryId] = useState<string | number | null>(null);
    const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
    const [editCategoryId, setEditCategoryId] = useState<string | number | null>(null);
    const [editCategoryName, setEditCategoryName] = useState("");
    const [editCategoryNameError, setEditCategoryNameError] = useState("");
    const [orderedCategories, setOrderedCategories] = useState<any[]>([]);
    const [showSaveOrderModal, setShowSaveOrderModal] = useState(false);

    useEffect(() => {
        dispatch(getMenuCategories());
    }, [dispatch]);

    useEffect(() => {
        setOrderedCategories(sortCategoriesByOrder(categoriesList));
    }, [categoriesList]);

    const hasOrderChanges = useMemo(
        () => !areCategoryOrdersEqual(orderedCategories, sortCategoriesByOrder(categoriesList)),
        [categoriesList, orderedCategories]
    );

    const closeEditCategoryModal = () => {
        setShowEditCategoryModal(false);
        setEditCategoryId(null);
        setEditCategoryName("");
        setEditCategoryNameError("");
    };

    const openEditCategoryModal = (category: any) => {
        const identifier = getCategoryId(category);
        setEditCategoryId(identifier ?? null);
        setEditCategoryName(getCategoryName(category));
        setEditCategoryNameError("");
        setShowEditCategoryModal(true);
    };

    const handleDeleteCategory = async () => {
        if (!deleteCategoryId) {
            return;
        }

        try {
            const response = await dispatch(deleteMenuCategory(deleteCategoryId)).unwrap();
            dispatch(
                showAlertWithTimeout("success", "Thành công", resolveApiMessage(response, { status: 200 }))
            );
            setDeleteCategoryId(null);
            dispatch(getMenuCategories());
        } catch (error) {
            dispatch(
                showAlertWithTimeout(
                    "danger",
                    "Thất bại",
                    resolveApiMessage(error)
                )
            );
        }
    };

    const handleAddCategory = async () => {
        const trimmedName = newCategoryName.trim();

        if (trimmedName.length === 0) {
            setCategoryNameError("Vui lòng nhập tên danh mục");
            return;
        }

        if (trimmedName.length > CATEGORY_NAME_MAX_LENGTH) {
            setCategoryNameError(`Tên danh mục không được vượt quá ${CATEGORY_NAME_MAX_LENGTH} ký tự`);
            return;
        }

        try {
            const response = await dispatch(createMenuCategory({ name: trimmedName })).unwrap();
            closeAddCategoryModal();
            dispatch(getMenuCategories());
            dispatch(
                showAlertWithTimeout("success", "Thành công", resolveApiMessage(response, { status: 200 }))
            );
        } catch (error) {
            const message = resolveApiMessage(error);
            setCategoryNameError(message);
            dispatch(
                showAlertWithTimeout(
                    "danger",
                    "Thất bại",
                    message
                )
            );
        }
    };

    const moveCategory = (fromIndex: number, toIndex: number) => {
        if (toIndex < 0 || toIndex >= orderedCategories.length) {
            return;
        }

        setOrderedCategories((currentCategories) => {
            const nextCategories = [...currentCategories];
            const [category] = nextCategories.splice(fromIndex, 1);
            nextCategories.splice(toIndex, 0, category);
            return nextCategories;
        });
    };

    const handleSaveCategoryOrder = async () => {
        const categoryIds = orderedCategories
            .map((category) => getCategoryId(category))
            .filter((categoryId): categoryId is string | number => categoryId !== undefined);

        if (categoryIds.length !== orderedCategories.length) {
            dispatch(showAlertWithTimeout("danger", "Thất bại", "Danh sách danh mục không hợp lệ."));
            return;
        }

        try {
            const response = await dispatch(reorderMenuCategories({ categoryIds })).unwrap();
            setShowSaveOrderModal(false);
            dispatch(showAlertWithTimeout("success", "Thành công", resolveApiMessage(response, { status: 200 })));
        } catch (error) {
            dispatch(showAlertWithTimeout("danger", "Thất bại", resolveApiMessage(error)));
        }
    };

    const handleEditCategory = async () => {
        if (!editCategoryId) {
            setEditCategoryNameError("Không tìm thấy danh mục cần sửa.");
            return;
        }

        const trimmedName = editCategoryName.trim();

        if (trimmedName.length === 0) {
            setEditCategoryNameError("Vui lòng nhập tên danh mục");
            return;
        }

        if (trimmedName.length > CATEGORY_NAME_MAX_LENGTH) {
            setEditCategoryNameError(
                `Tên danh mục không được vượt quá ${CATEGORY_NAME_MAX_LENGTH} ký tự`
            );
            return;
        }

        try {
            const response = await dispatch(
                updateMenuCategory({
                    categoryId: editCategoryId,
                    data: { name: trimmedName },
                })
            ).unwrap();
            closeEditCategoryModal();
            dispatch(getMenuCategories());
            dispatch(
                showAlertWithTimeout("success", "Thành công", resolveApiMessage(response, { status: 200 }))
            );
        } catch (error) {
            const message = resolveApiMessage(error);
            setEditCategoryNameError(message);
            dispatch(
                showAlertWithTimeout(
                    "danger",
                    "Thất bại",
                    message
                )
            );
        }
    };

    const renderTableContent = () => {
        if (loading) {
            return (
                <div className="cart-table card mb-3">
                    <div className="table-responsive card-body">
                        <table className="table mb-0">
                            <tbody>
                                <tr>
                                    <td colSpan={2} className="text-center py-3">
                                        Đang tải danh mục...
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        }

        if (orderedCategories.length === 0) {
            return (
                <div className="cart-table card mb-3">
                    <div className="table-responsive card-body">
                        <table className="table mb-0">
                            <tbody>
                                <tr>
                                    <td colSpan={2} className="text-center py-3">
                                        {error ?? "Chưa có danh mục nào."}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        }

        return orderedCategories.map((item: any, index: number) => {
            const categoryId = getCategoryId(item);
            const totalItems = getCategoryMenuCount(item);
            const displayCount = typeof totalItems === "number" && !Number.isNaN(totalItems) ? totalItems : 0;

            return (
                <div className="cart-table card mb-3" key={categoryId ?? index}>
                    <div className="table-responsive card-body">
                        <table className="table mb-0">
                            <tbody>
                                <tr>
                                    <td>
                                        <Link className="product-title" to="#">
                                            {getCategoryName(item)}
                                            <span className="mt-1">{displayCount} món</span>
                                        </Link>
                                    </td>
                                    <td className="text-end align-middle">
                                        {index > 0 && (
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-sm text-white me-1"
                                                onClick={() => moveCategory(index, index - 1)}
                                                title="Di chuyển lên"
                                            >
                                                <i className="mdi mdi-chevron-up"></i>
                                            </button>
                                        )}
                                        {index < orderedCategories.length - 1 && (
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-sm text-white me-1"
                                                onClick={() => moveCategory(index, index + 1)}
                                                title="Di chuyển xuống"
                                            >
                                                <i className="mdi mdi-chevron-down"></i>
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm text-white me-1"
                                            onClick={() => {
                                                openEditCategoryModal(item);
                                            }}
                                        >
                                            <i className="mdi mdi-lead-pencil"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm text-white"
                                            title={displayCount > 0 ? "Không thể xóa danh mục đang có món" : "Xóa danh mục"}
                                            onClick={() => {
                                                if (displayCount > 0) {
                                                    dispatch(
                                                        showAlertWithTimeout(
                                                            "warning",
                                                            "Không thể xóa danh mục",
                                                            `Danh mục này đang có ${displayCount} món. Vui lòng chuyển món sang danh mục khác trước.`
                                                        )
                                                    );
                                                    return;
                                                }

                                                setDeleteCategoryId(categoryId ?? null);
                                                setShowDeleteCategoryModal(true);
                                            }}
                                        >
                                            <i className="mdi mdi-delete"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        });
    };

    return (
        <React.Fragment>
            <ConfirmModal
                isOpen={showSaveOrderModal}
                typeConfirm="primary"
                title="Xác nhận lưu"
                content="Bạn có chắc chắn muốn lưu thứ tự danh mục mới không?"
                onConfirm={handleSaveCategoryOrder}
                onCancel={() => setShowSaveOrderModal(false)}
            />
            <ConfirmModal
                isOpen={showDeleteCategoryModal}
                typeConfirm="danger"
                title="Xác nhận xóa"
                content="Bạn có chắc chắn muốn xóa danh mục này không?"
                onConfirm={() => {
                    setShowDeleteCategoryModal(false);
                    handleDeleteCategory();
                }}
                onCancel={() => {
                    setShowDeleteCategoryModal(false);
                    setDeleteCategoryId(null);
                }}
            />
            <Modal
                size="sm"
                isOpen={showAddCategoryModal}
                toggle={closeAddCategoryModal}
                centered
            >
                <ModalBody>
                    <h6 className="fs-15">Vui lòng nhập tên danh mục</h6>
                    <div className="mb-3">
                        <input
                            className="form-control"
                            type="text"
                            id="cardholder"
                            placeholder="VD: Cà phê, Trà, Nước ép, Sinh tố, ..."
                            value={newCategoryName}
                            maxLength={CATEGORY_NAME_MAX_LENGTH}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setNewCategoryName(event.target.value);
                                if (categoryNameError) {
                                    setCategoryNameError("");
                                }
                            }}
                        />
                        {categoryNameError && (
                            <div className="invalid-feedback d-block">{categoryNameError}</div>
                        )}
                    </div>
                </ModalBody>
                <div className="modal-footer">
                    <Button color="secondary" onClick={closeAddCategoryModal}>Hủy</Button>
                    <Button color="primary" onClick={handleAddCategory}>
                        Thêm
                    </Button>
                </div>
            </Modal>

            <Modal
                size="sm"
                isOpen={showEditCategoryModal}
                toggle={closeEditCategoryModal}
                centered
            >
                <ModalBody>
                    <h6 className="fs-15">Chỉnh sửa tên danh mục</h6>
                    <div className="mb-3">
                        <input
                            className="form-control"
                            type="text"
                            placeholder="VD: Cà phê, Trà, Nước ép, Sinh tố, ..."
                            value={editCategoryName}
                            maxLength={CATEGORY_NAME_MAX_LENGTH}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setEditCategoryName(event.target.value);
                                if (editCategoryNameError) {
                                    setEditCategoryNameError("");
                                }
                            }}
                        />
                        {editCategoryNameError && (
                            <div className="invalid-feedback d-block">{editCategoryNameError}</div>
                        )}
                    </div>
                </ModalBody>
                <div className="modal-footer">
                    <Button color="secondary" onClick={closeEditCategoryModal}>Hủy</Button>
                    <Button color="primary" onClick={handleEditCategory}>
                        Lưu
                    </Button>
                </div>
            </Modal>

            <div className="header-area" id="headerArea">
                <div
                    className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <HomeLogoLink className="me-2" />

                    <div className="page-heading">
                        <h6 className="mb-0">Danh mục món</h6>
                    </div>

                    <div className="d-flex align-items-center">
                        <div
                            className="filter-option"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#suhaFilterOffcanvas"
                            aria-controls="suhaFilterOffcanvas"
                        >
                            <i onClick={openAddCategoryModal} className="mdi mdi-plus"></i>
                        </div>
                        {hasOrderChanges && (
                            <button
                                type="button"
                                className="btn btn-success btn-sm text-white ms-2"
                                onClick={() => setShowSaveOrderModal(true)}
                            >
                                Lưu
                            </button>
                        )}
                        <HeaderSidebarToggle className="ms-2" />
                    </div>
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container">
                    <div className="cart-wrapper-area py-3">
                        {renderTableContent()}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default MenuCategory;
